import { amplifyProxy } from '@/services/directApi'

export default {
  namespaced: true,
  state: {
    loading: {
      bank: false,
      payments: false,
      oneOff: false,
      sto: false,
      cancel: false,
      requested: false,
      partiallyPaid: false,
      fullyPaid: false,
      cancelled: false,
      stoSetup: false,
      all: false
    },
    error: {
      bank: null,
      payments: null,
      oneOff: null,
      sto: null,
      cancel: null,
      requested: null,
      partiallyPaid: null,
      fullyPaid: null,
      cancelled: null,
      stoSetup: null,
      all: null
    },
    paymentRequests: {
      requested: [],
      partiallyPaid: [],
      fullyPaid: [],
      cancelled: [],
      stoSetup: [],
      all: []
    },
    totalCounts: {
      requested: 0,
      partiallyPaid: 0,
      fullyPaid: 0,
      cancelled: 0,
      stoSetup: 0,
      all: 0
    }
  },
  mutations: {
    SET_LOADING(state, { type, value }) {
      state.loading[type] = value
    },
    SET_ERROR(state, { type, error }) {
      state.error[type] = error
    },
    SET_PAYMENT_REQUESTS(state, { type, data }) {
      state.paymentRequests[type] = data
    },
    SET_TOTAL_COUNT(state, { type, count }) {
      state.totalCounts[type] = count
    }
  },
  actions: {
    async getBankAccounts({ commit, rootState }) {
      commit('SET_LOADING', { type: 'bank', value: true })
      commit('SET_ERROR', { type: 'bank', error: null })

      try {
        const response = await amplifyProxy.get(`/${rootState.user.ClientName}/company/bankaccounts`, {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID
          }
        })
        return response
      } catch (error) {
        commit('SET_ERROR', { type: 'bank', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'bank', value: false })
      }
    },

    async getPaymentRequests({ commit, rootState }, payload) {
      commit('SET_LOADING', { type: 'payments', value: true })
      commit('SET_ERROR', { type: 'payments', error: null })

      try {
        const offset = payload.offset || 0
        const count = payload.count || 100
        const queryParams = {
          showOneOffPayments: true,
          showStoPayments: true,
          from: '2000-09-21T08:35:00.000Z',
          to: payload.dateTo,
          personID: payload.personID,
          branchID: rootState.branchID
        }

        // Add optional state parameter
        if (payload.state) {
          queryParams.state = payload.state
        }

        const response = await amplifyProxy.get(`/${rootState.user.ClientName}/payments/all/${offset}/${count}`, {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: queryParams
        })
        return response
      } catch (error) {
        commit('SET_ERROR', { type: 'payments', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'payments', value: false })
      }
    },

    async createOneOffPayment({ commit, rootState }, payload) {
      commit('SET_LOADING', { type: 'oneOff', value: true })
      commit('SET_ERROR', { type: 'oneOff', error: null })

      try {
        const submissionModel = {
          Description: payload.description,
          Amount: payload.amount,
          EntryType: payload.entryType
        }

        const response = await amplifyProxy.post(`/${rootState.user.ClientName}/payments/oneoff/${payload.personID}`, submissionModel, {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            accountID: payload.accountID
          }
        })
        return response
      } catch (error) {
        commit('SET_ERROR', { type: 'oneOff', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'oneOff', value: false })
      }
    },

    async createStoPayment({ commit, rootState }, payload) {
      commit('SET_LOADING', { type: 'sto', value: true })
      commit('SET_ERROR', { type: 'sto', error: null })

      try {
        const submissionModel = {
          FirstPayment: payload.firstPayment,
          LastPayment: payload.lastPayment,
          Frequency: payload.frequency,
          Description: payload.description,
          Amount: payload.amount,
          EntryType: payload.entryType
        }

        const response = await amplifyProxy.post(`/${rootState.user.ClientName}/payments/sto/${payload.personID}`, submissionModel, {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            accountID: payload.accountID
          }
        })
        return response
      } catch (error) {
        commit('SET_ERROR', { type: 'sto', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'sto', value: false })
      }
    },

    async cancelPaymentRequest({ commit, rootState }, paymentReference) {
      commit('SET_LOADING', { type: 'cancel', value: true })
      commit('SET_ERROR', { type: 'cancel', error: null })

      try {
        const response = await amplifyProxy.del(`/${rootState.user.ClientName}/payments/cancel/${paymentReference}`, {
          headers: {
            token: rootState.user.LoginToken,

          }
        })
        return response
      } catch (error) {
        commit('SET_ERROR', { type: 'cancel', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'cancel', value: false })
      }
    },

    // Actions for specific payment states
    async getRequestedPayments({ commit, rootState }, { offset = 0, count = 10, personId = '', fromDate = '', toDate = '' } = {}) {
      commit('SET_LOADING', { type: 'requested', value: true })
      commit('SET_ERROR', { type: 'requested', error: null })

      try {
        const queryParams = {
          showOneOffPayments: true,
          showStoPayments: true,
          from: fromDate || '2000-09-21T08:35:00.000Z',
          to: toDate || new Date().toISOString(),
          state: 'Requested',
          branchID: rootState.branchID
        }

        // Add personId if provided
        if (personId) queryParams.personId = personId

        const response = await amplifyProxy.get(`/${rootState.user.ClientName}/payments/all/${offset}/${count}`, {
          headers: {
            token: rootState.user.LoginToken,
          },
          queryStringParameters: queryParams
        })
        const data = response.data?.Data || response.data || []
        const totalCount = response.data?.Count || data.length

        commit('SET_PAYMENT_REQUESTS', { type: 'requested', data })
        commit('SET_TOTAL_COUNT', { type: 'requested', count: totalCount })

        return { data, count: totalCount }
      } catch (error) {
        commit('SET_ERROR', { type: 'requested', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'requested', value: false })
      }
    },

    async getPartiallyPaidPayments({ commit, rootState }, { offset = 0, count = 10, personId = '', fromDate = '', toDate = '' } = {}) {
      commit('SET_LOADING', { type: 'partiallyPaid', value: true })
      commit('SET_ERROR', { type: 'partiallyPaid', error: null })
      
      try {
        const queryParams = {
          showOneOffPayments: true,
          showStoPayments: true,
          from: fromDate || '2000-09-21T08:35:00.000Z',
          to: toDate || new Date().toISOString(),
          state: 'PartiallyPaid',
          branchID: rootState.branchID
        }

        // Add personId if provided
        if (personId) queryParams.personId = personId

        const response = await amplifyProxy.get(`/${rootState.user.ClientName}/payments/all/${offset}/${count}`, {
          headers: {
            token: rootState.user.LoginToken,
          },
          queryStringParameters: queryParams
        })
        const data = response.data?.Data || response.data || []
        const totalCount = response.data?.Count || data.length
        
        commit('SET_PAYMENT_REQUESTS', { type: 'partiallyPaid', data })
        commit('SET_TOTAL_COUNT', { type: 'partiallyPaid', count: totalCount })
        
        return { data, count: totalCount }
      } catch (error) {
        commit('SET_ERROR', { type: 'partiallyPaid', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'partiallyPaid', value: false })
      }
    },

    async getFullyPaidPayments({ commit, rootState }, { offset = 0, count = 10, personId = '', fromDate = '', toDate = '' } = {}) {
      commit('SET_LOADING', { type: 'fullyPaid', value: true })
      commit('SET_ERROR', { type: 'fullyPaid', error: null })

      try {
        const queryParams = {
          showOneOffPayments: true,
          showStoPayments: true,
          from: fromDate || '2000-09-21T08:35:00.000Z',
          to: toDate || new Date().toISOString(),
          state: 'FullyPaid',
          branchID: rootState.branchID
        }

        // Add personId if provided
        if (personId) queryParams.personId = personId

        const response = await amplifyProxy.get(`/${rootState.user.ClientName}/payments/all/${offset}/${count}`, {
          headers: {
            token: rootState.user.LoginToken,
          },
          queryStringParameters: queryParams
        })
        const data = response.data?.Data || response.data || []
        const totalCount = response.data?.Count || data.length
        
        commit('SET_PAYMENT_REQUESTS', { type: 'fullyPaid', data })
        commit('SET_TOTAL_COUNT', { type: 'fullyPaid', count: totalCount })
        
        return { data, count: totalCount }
      } catch (error) {
        commit('SET_ERROR', { type: 'fullyPaid', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'fullyPaid', value: false })
      }
    },

    async getCancelledPayments({ commit, rootState }, { offset = 0, count = 10, personId = '', fromDate = '', toDate = '' } = {}) {
      commit('SET_LOADING', { type: 'cancelled', value: true })
      commit('SET_ERROR', { type: 'cancelled', error: null })

      try {
        const queryParams = {
          showOneOffPayments: true,
          showStoPayments: true,
          from: fromDate || '2000-09-21T08:35:00.000Z',
          to: toDate || new Date().toISOString(),
          state: 'Cancelled',
          branchID: rootState.branchID
        }

        // Add personId if provided
        if (personId) queryParams.personId = personId

        const response = await amplifyProxy.get(`/${rootState.user.ClientName}/payments/all/${offset}/${count}`, {
          headers: {
            token: rootState.user.LoginToken,
          },
          queryStringParameters: queryParams
        })
        const data = response.data?.Data || response.data || []
        const totalCount = response.data?.Count || data.length
        
        commit('SET_PAYMENT_REQUESTS', { type: 'cancelled', data })
        commit('SET_TOTAL_COUNT', { type: 'cancelled', count: totalCount })
        
        return { data, count: totalCount }
      } catch (error) {
        commit('SET_ERROR', { type: 'cancelled', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'cancelled', value: false })
      }
    },

    async getStoSetupPayments({ commit, rootState }, { offset = 0, count = 10, personId = '', fromDate = '', toDate = '' } = {}) {
      commit('SET_LOADING', { type: 'stoSetup', value: true })
      commit('SET_ERROR', { type: 'stoSetup', error: null })

      try {
        const queryParams = {
          showOneOffPayments: true,
          showStoPayments: true,
          from: fromDate || '2000-09-21T08:35:00.000Z',
          to: toDate || new Date().toISOString(),
          state: 'StoSetup',
          branchID: rootState.branchID
        }

        // Add personId if provided
        if (personId) queryParams.personId = personId

        const response = await amplifyProxy.get(`/${rootState.user.ClientName}/payments/all/${offset}/${count}`, {
          headers: {
            token: rootState.user.LoginToken,
          },
          queryStringParameters: queryParams
        })
        const data = response.data?.Data || response.data || []
        const totalCount = response.data?.Count || data.length
        
        commit('SET_PAYMENT_REQUESTS', { type: 'stoSetup', data })
        commit('SET_TOTAL_COUNT', { type: 'stoSetup', count: totalCount })
        
        return { data, count: totalCount }
      } catch (error) {
        commit('SET_ERROR', { type: 'stoSetup', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'stoSetup', value: false })
      }
    },

    async getAllPayments({ commit, rootState }, { offset = 0, count = 10, personId = '', fromDate = '', toDate = '' } = {}) {
      commit('SET_LOADING', { type: 'all', value: true })
      commit('SET_ERROR', { type: 'all', error: null })

      try {
        const queryParams = {
          showOneOffPayments: true,
          showStoPayments: true,
          from: fromDate || '2000-09-21T08:35:00.000Z',
          to: toDate || new Date().toISOString(),
          // No state filter for 'all' - returns all payment requests
          branchID: rootState.branchID
        }

        // Add personId if provided
        if (personId) queryParams.personId = personId

        const response = await amplifyProxy.get(`/${rootState.user.ClientName}/payments/all/${offset}/${count}`, {
          headers: {
            token: rootState.user.LoginToken,
          },
          queryStringParameters: queryParams
        })
        const data = response.data?.Data || response.data || []
        const totalCount = response.data?.Count || data.length
        
        commit('SET_PAYMENT_REQUESTS', { type: 'all', data })
        commit('SET_TOTAL_COUNT', { type: 'all', count: totalCount })
        
        return { data, count: totalCount }
      } catch (error) {
        commit('SET_ERROR', { type: 'all', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'all', value: false })
      }
    }
  }
}